/**
 * Which pages session replay may record.
 *
 * ## Why an allowlist
 *
 * The obvious rule — "record visitors who aren't logged in" — is wrong in this
 * product, and wrong in the direction that costs us. Two surfaces are anonymous
 * and still full of other people's content:
 *
 *  - `/apps/<username>/<slug>` — deployed apps, public by design
 *    (`isPublicDeployment` in lib/spa-auth.ts), rendering a CUSTOMER's app to
 *    whoever visits.
 *  - `/a/<slug>` and friends redirect to login rather than 404, so a
 *    "not logged in yet" visitor can hold such a URL mid-redirect.
 *
 * A blocklist fails open: the next route somebody adds is recorded until
 * somebody notices, which is exactly how replay came to cover the whole signed-in
 * app. An allowlist fails closed — a new route records nothing until it is added
 * here on purpose.
 *
 * ## What is deliberately IN
 *
 * The landing page, including the pre-auth discovery chat widget. That is a
 * decision, not an oversight: it is the conversion moment we most want to watch,
 * and everything typed and rendered there is masked (`maskAllInputs` +
 * `maskTextSelector: "*"`, enforced server-side as well as in the client). We
 * keep layout, clicks and rage-clicks; we never keep the words.
 */

import { splitLocalePath } from "./i18n/locales";

/**
 * First path segment of every page replay may record. `""` is the landing page.
 *
 * Everything absent is excluded, including surfaces that merely LOOK public:
 * `apps` (customer app content), `tg` (a redirector, no page), `get-started`
 * (branches on session and leads into auth), and every authenticated route.
 */
const MARKETING_ROOTS: ReadonlySet<string> = new Set([
  "",
  "about",
  "blog",
  "business",
  // Customer testimonials. Published stories the customers approved — our
  // pitch in their words, no account data and no signed-in surface.
  "customers",
  // Profession segment pages (`/for/<profession>`). Pure pitch — no customer
  // content, no signed-in surface — and the whole point of them is learning
  // where an unfamiliar audience stops reading.
  "for",
  "integrations",
  "pricing",
  "privacy",
  "terms",
]);

/**
 * Strip a locale prefix the way proxy.ts does.
 *
 * `/sv/pricing` is a REWRITE, not a redirect — the route resolves to `/pricing`
 * but the browser URL (and therefore `usePathname()`) keeps the prefix. Sharing
 * `splitLocalePath` with proxy.ts rather than hardcoding "sv" means a new
 * locale cannot silently stop matching and turn recording off on half the
 * marketing site. It also covers the explicit `/en/` prefix, which would
 * otherwise read as a root segment named "en" and disable replay.
 */
const stripLocale = (pathname: string): string => splitLocalePath(pathname).path;

/** Whether this path is a marketing page replay may record. */
export function isMarketingPath(pathname: string): boolean {
  const path = stripLocale(pathname);
  if (!path.startsWith("/")) return false;
  const root = path.split("/")[1] ?? "";
  return MARKETING_ROOTS.has(root);
}
